import React, { useEffect, useState, useMemo, useCallback, useLayoutEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { Link, useOutletContext, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import Toastify from "../../utils/tostfy";
import { endpoint } from "../../utils/endpoints";
import { normalizeSearchInput, SEARCH_MAX_LENGTH } from "../../utils/searchInput";
import EyeIcon from "../../assets/image/eye-icon.svg";
import EditIcon from "../../assets/image/edit-icon.svg";
import LeftAngle from "../../assets/image/left-angle.svg";
import RightAngle from "../../assets/image/right-angle.svg";
import { useListPersistence } from "../../hooks/useListPersistence";
import { readListLimit, readListPage } from "../../hooks/useListQueryPersistence";
import { useBackToList } from "../../hooks/useBackToList";
import { formatInr } from "../../utils/formatAmount";
import { tripStatusLabel } from "../../utils/tripStatus";
import PageNumberInput from "../../Components/Pagination/PageNumberInput";

const VIEW_TRIP_PATH = "/trip-assignment-management/view-trip";
const LIST_PATH = "/trip-assignment-management";

const ACCOUNTANT_API =
  process.env.REACT_APP_ACCOUNTANT_API;

const parseBody = async (response) => {
  const text = await response.text();
  if (!text) return null;
  try {
    return JSON.parse(text);
  } catch {
    return { rawMessage: text };
  }
};

const getErrorMessage = (payload, response) => {
  if (!payload || typeof payload !== "object") {
    return response.statusText || "Something went wrong. Please try again.";
  }
  return (
    payload.errorMessage ||
    payload.message ||
    payload.error ||
    (Array.isArray(payload.errors) ? payload.errors.join(", ") : null) ||
    `Request failed (${response.status})`
  );
};

const CHALLAN_MAX_LENGTH = 30;

/** Challan number: alphanumeric only and must include both letters and numbers. */
const normalizeChallanInput = (raw) =>
  String(raw ?? "")
    .replace(/^\s+/, "")
    .replace(/[^a-zA-Z0-9]/g, "")
    .slice(0, CHALLAN_MAX_LENGTH);

const isValidChallanNumber = (value) => {
  const challan = String(value || "").trim();
  return /^[A-Za-z0-9]+$/.test(challan) && /[A-Za-z]/.test(challan) && /\d/.test(challan);
};

const mapTripListRow = (t) => {
  const v = t.vehicle_id;
  const vehicleName =
    v && typeof v === "object" && v != null
      ? v.name ?? v.registration_number ?? "—"
      : "—";
  const challanNumber = normalizeChallanInput(t.challan_number);
  const challanMissingOrInvalid = !isValidChallanNumber(challanNumber);
  return {
    id: t._id,
    tripId:
      t.trip_ref_id != null && t.trip_ref_id !== "" ? `${t.trip_ref_id}` : "—",
    companyLocation: t.company_id?.company_location_id?.name ?? "—",
    miplLocation: t.mipl_location_id?.name ?? "—",
    vehicle: vehicleName,
    challanNumber,
    needsChallanUpdate: challanMissingOrInvalid,
    status: tripStatusLabel(t.status),
    raw: t,
  };
};

const ViewTrip = () => {
  const { setSubheading } = useOutletContext();
  const [searchParams] = useSearchParams();
  const routeId = searchParams.get("route_id")?.trim() || "";
  const backToList = useBackToList(LIST_PATH);

  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(!!routeId);
  const [detailError, setDetailError] = useState(false);

  useEffect(() => {
    setSubheading("View trip");
    return () => {
      setSubheading("");
    };
  }, [setSubheading]);

  useEffect(() => {
    if (!routeId) {
      setDetail(null);
      setDetailLoading(false);
      setDetailError(false);
      return;
    }

    let cancelled = false;

    const loadDetail = async () => {
      const token = localStorage.getItem("sessionToken");
      if (!token) {
        toast.error("Session expired. Please log in again.");
        setDetailLoading(false);
        setDetailError(true);
        return;
      }

      setDetailLoading(true);
      setDetailError(false);

      const url = `${ACCOUNTANT_API}${endpoint.viewRouteDetails}?route_id=${encodeURIComponent(
        routeId
      )}`;

      try {
        const response = await fetch(url, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        const payload = await parseBody(response);

        if (cancelled) return;

        if (!response.ok) {
          toast.error(getErrorMessage(payload, response));
          setDetail(null);
          setDetailError(true);
          return;
        }

        const apiCode = payload?.code;
        if (apiCode != null && apiCode !== 200 && apiCode !== 201) {
          toast.error(getErrorMessage(payload, response));
          setDetail(null);
          setDetailError(true);
          return;
        }

        const data = payload?.data;
        setDetail(data && typeof data === "object" ? data : null);
        if (!data || typeof data !== "object") setDetailError(true);
      } catch (err) {
        console.error(err);
        if (!cancelled) {
          toast.error(
            err?.message === "Failed to fetch"
              ? "Network error. Check your connection."
              : "Could not load route details."
          );
          setDetail(null);
          setDetailError(true);
        }
      } finally {
        if (!cancelled) setDetailLoading(false);
      }
    };

    loadDetail();
    return () => {
      cancelled = true;
    };
  }, [routeId]);

  const summary = useMemo(() => {
    if (!detail) return null;
    const company = detail.company_id;
    const statusNum = detail.status === 1 || detail.status === "1" ? 1 : 0;
    return {
      companyName: company?.name ?? "—",
      companyLocation: company?.company_location_id?.name ?? "—",
      miplLocation: detail.mipl_location_id?.name ?? "—",
      routeRef:
        detail.route_ref_id != null && detail.route_ref_id !== ""
          ? `${detail.route_ref_id}`
          : "—",
      idealAverage:
        detail.ideal_average != null && !Number.isNaN(Number(detail.ideal_average))
          ? `${Number(detail.ideal_average)} average`
          : "—",
      singleDriver: formatInr(detail.single_trip_amount),
      dualDriver: formatInr(detail.dual_driver_amount),
      statusLabel: statusNum === 1 ? "Active" : "Inactive",
    };
  }, [detail]);

  const [search, setSearch] = useState(() => searchParams.get("search") || "");
  const [debouncedSearch, setDebouncedSearch] = useState(() =>
    normalizeSearchInput(searchParams.get("search") || "").trim()
  );
  const [currentPage, setCurrentPage] = useState(() => readListPage(searchParams));
  const [itemsPerPage, setItemsPerPage] = useState(() => readListLimit(searchParams));
  const [trips, setTrips] = useState([]);
  const [tripsLoading, setTripsLoading] = useState(false);
  const [totalRecords, setTotalRecords] = useState(0);
  const [apiTotalPages, setApiTotalPages] = useState(1);

  const [challanModalOpen, setChallanModalOpen] = useState(false);
  const [challanModalTripId, setChallanModalTripId] = useState(null);
  const [challanInput, setChallanInput] = useState("");
  const [challanError, setChallanError] = useState("");
  const [challanSaving, setChallanSaving] = useState(false);
  const [listRefreshKey, setListRefreshKey] = useState(0);

  const applyRestoredState = useCallback(
    (saved) => {
      if (saved.route_id && routeId && saved.route_id !== routeId) return;
      if (saved.search != null) {
        setSearch(saved.search);
        setDebouncedSearch(normalizeSearchInput(saved.search).trim());
      }
      if (saved.page) {
        const p = Number(saved.page);
        if (Number.isFinite(p) && p > 0) setCurrentPage(p);
      }
      if (saved.limit) {
        const l = Number(saved.limit);
        if (Number.isFinite(l) && l > 0) setItemsPerPage(l);
      }
    },
    [routeId]
  );

  const { skipPageResetRef, detailListQuery } = useListPersistence(
    VIEW_TRIP_PATH,
    {
      route_id: routeId || undefined,
      page: currentPage,
      limit: itemsPerPage,
      search: debouncedSearch || undefined,
    },
    applyRestoredState
  );

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 400);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    if (!routeId) {
      setTrips([]);
      setTotalRecords(0);
      setApiTotalPages(1);
      return;
    }

    let cancelled = false;

    const loadTrips = async () => {
      const token = localStorage.getItem("sessionToken");
      if (!token) {
        setTrips([]);
        return;
      }

      setTripsLoading(true);
      const params = new URLSearchParams({
        search: debouncedSearch.trim(),
        page: String(currentPage),
        limit: String(itemsPerPage),
        
        route_id: routeId,
      });
      const url = `${ACCOUNTANT_API}${endpoint.listOfTrip}?${params.toString()}`;

      try {
        const response = await fetch(url, {
          method: "GET",
          headers: { Authorization: `Bearer ${token}` },
        });
        const payload = await parseBody(response);
        if (cancelled) return;

        if (!response.ok) {
          toast.error(getErrorMessage(payload, response));
          setTrips([]);
          setTotalRecords(0);
          setApiTotalPages(1);
          return;
        }

        const apiCode = payload?.code;
        if (apiCode != null && apiCode !== 200 && apiCode !== 201) {
          toast.error(getErrorMessage(payload, response));
          setTrips([]);
          setTotalRecords(0);
          setApiTotalPages(1);
          return;
        }

        const data = payload?.data || {};
        const list = Array.isArray(data.trip_data) ? data.trip_data : [];

        setTrips(list.map(mapTripListRow));
        setTotalRecords(typeof data.total === "number" ? data.total : list.length);
        setApiTotalPages(
          typeof data.total_pages === "number" && data.total_pages > 0
            ? data.total_pages
            : 1
        );
      } catch (err) {
        console.error(err);
        if (!cancelled) {
          toast.error(
            err?.message === "Failed to fetch"
              ? "Network error. Check your connection."
              : "Could not load trips."
          );
          setTrips([]);
          setTotalRecords(0);
          setApiTotalPages(1);
        }
      } finally {
        if (!cancelled) setTripsLoading(false);
      }
    };

    loadTrips();
    return () => {
      cancelled = true;
    };
  }, [routeId, currentPage, itemsPerPage, debouncedSearch, listRefreshKey]);

  useLayoutEffect(() => {
    if (skipPageResetRef.current) return;
    setCurrentPage(1);
  }, [debouncedSearch, routeId, skipPageResetRef]);

  const indexOfFirst = (currentPage - 1) * itemsPerPage;
  const totalPages = apiTotalPages;

  const addTripQuery =
    routeId !== "" ? `?route_id=${encodeURIComponent(routeId)}` : "";
  const viewDetailsBase = `/trip-assignment-management/view-trip-details`;
  const viewTripReturnQuery = detailListQuery || (routeId ? `&returnTo=${encodeURIComponent(`${VIEW_TRIP_PATH}?route_id=${encodeURIComponent(routeId)}`)}` : "");

  const openChallanModal = (tripId) => {
    setChallanModalTripId(tripId);
    setChallanInput("");
    setChallanError("");
    setChallanModalOpen(true);
  };

  const closeChallanModal = () => {
    setChallanModalOpen(false);
    setChallanModalTripId(null);
    setChallanInput("");
    setChallanError("");
  };

  const currentChallanValue = normalizeChallanInput(challanInput);
  const canSubmitChallan = isValidChallanNumber(currentChallanValue);

  const submitChallanUpdate = async () => {
    const trimmed = currentChallanValue;
    if (!trimmed || !isValidChallanNumber(trimmed)) {
      setChallanError("Challan number must be alphanumeric and include both letters and numbers.");
      return;
    }
    if (!challanModalTripId) return;

    const token = localStorage.getItem("sessionToken");
    if (!token) {
      toast.error("Session expired. Please log in again.");
      return;
    }

    setChallanSaving(true);
    try {
      const response = await fetch(
        `${ACCOUNTANT_API}${endpoint.updateChallanNumber}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            trip_id: challanModalTripId,
            challan_number: trimmed,
          }),
        }
      );
      const payload = await parseBody(response);

      if (!response.ok) {
        toast.error(getErrorMessage(payload, response));
        return;
      }

      const apiCode = payload?.code;
      if (apiCode != null && apiCode !== 200 && apiCode !== 201) {
        toast.error(getErrorMessage(payload, response));
        return;
      }

      toast.success(payload?.message || "Challan number updated.");
      closeChallanModal();
      setListRefreshKey((k) => k + 1);
    } catch (err) {
      console.error(err);
      toast.error(
        err?.message === "Failed to fetch"
          ? "Network error. Check your connection."
          : "Could not update challan number."
      );
    } finally {
      setChallanSaving(false);
    }
  };

  return (
    <>
      <div className="mt-7">
        <div className="flex items-center">
          <Link to={backToList} className="btn btn-primary">
            Back
          </Link>
          <Link
            to={`/trip-assignment-management/add-new-trip${addTripQuery}`}
            className="btn btn-danger ml-auto"
          >
            Assign Driver To Trip
          </Link>
        </div>

        {!routeId ? (
          <div className="rounded-lg px-4 py-6 bg-white mt-5 shadow-sm text-center text-gray-600">
            No route selected.{" "}
            <Link to={backToList} className="text-primary underline">
              Pick a route from the list
            </Link>
            .
          </div>
        ) : detailLoading ? (
          <div className="rounded-lg px-4 py-8 bg-white mt-5 shadow-sm text-center text-gray-500">
            Loading route details…
          </div>
        ) : detailError || !summary ? (
          <div className="rounded-lg px-4 py-6 bg-white mt-5 shadow-sm text-center text-gray-600">
            Could not load details for this route.{" "}
            <Link to={backToList} className="text-primary underline">
              Back to routes
            </Link>
          </div>
        ) : (
          <div className="rounded-lg px-4 py-2 bg-white mb-4 shadow-sm !mt-5">
            <ul className="grid grid-cols-2 [&_li]:flex [&_li]:py-1.5 [&_li]:justify-between [&_li:nth-child(odd)]:pr-12 [&_li:nth-child(odd)]:border-r [&_li:nth-child(even)]:pl-12 font-UbuntuMedium [&_.label]:text-gray-400">
              <li>
                <div className="label">Route ID</div>
                <div className="font-UbuntuMedium">{summary.routeRef}</div>
              </li>
              <li>
                <div className="label">Company Name</div>
                <div className="font-UbuntuMedium">{summary.companyName}</div>
              </li>
              <li>
                <div className="label">Company Location</div>
                <div className="font-UbuntuMedium">{summary.companyLocation}</div>
              </li>
              <li>
                <div className="label">MIPL Location</div>
                <div className="font-UbuntuMedium">{summary.miplLocation}</div>
              </li>
              <li>
                <div className="label">Status</div>
                <div className="font-UbuntuMedium">{summary.statusLabel}</div>
              </li>
              <li>
                <div className="label">Single Driver</div>
                <div className="font-UbuntuMedium">{summary.singleDriver}</div>
              </li>
              <li className="!border-r-0 !pr-0" aria-hidden="true"></li>
              <li>
                <div className="label">Dual Driver</div>
                <div className="font-UbuntuMedium">{summary.dualDriver}</div>
              </li>
            </ul>
          </div>
        )}

        <div className="flex gap-x-2.5 mb-4">
          <input
            type="text"
            placeholder="Search..."
            className="border px-3 py-2 rounded-lg outline-none text-sm searchIcon bg-white focus:!shadow-sm"
            value={search}
            maxLength={SEARCH_MAX_LENGTH}
            onChange={(e) => setSearch(normalizeSearchInput(e.target.value))}
            disabled={!routeId}
          />
        </div>

        <div className="common-table-wrapper m-0">
          <div className="table-responsive">
            <table>
              <thead>
                <tr>
                  <th>S.No.</th>
                  <th>Trip ID</th>
                  <th>Company Location</th>
                  <th>MIPL Location</th>
                  <th>Vehicle</th>
                  <th>Challan Number</th>
                  <th>Status</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {!routeId ? (
                  <tr>
                    <td colSpan={8} className="text-center py-8 text-gray-500">
                      Select a route to load trips.
                    </td>
                  </tr>
                ) : tripsLoading ? (
                  <tr>
                    <td colSpan={8} className="text-center py-8 text-gray-500">
                      Loading trips…
                    </td>
                  </tr>
                ) : trips.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="text-center py-8 text-gray-500">
                      No trips found.
                    </td>
                  </tr>
                ) : (
                  trips.map((row, index) => (
                    <tr key={row.id}>
                      <td>{indexOfFirst + index + 1}</td>
                      <td>{row.tripId}</td>
                      <td>{row.companyLocation}</td>
                      <td>{row.miplLocation}</td>
                      <td>{row.vehicle}</td>
                      <td className="align-middle">
                        {row.needsChallanUpdate ? (
                          <button
                            type="button"
                            onClick={() => openChallanModal(row.id)}
                            className="inline-flex items-center gap-1.5 rounded-full border border-gray-300 bg-gray-100 px-3 py-1.5 text-xs font-medium text-gray-800 hover:bg-gray-200"
                          >
                            Update Challan
                            <img src={EditIcon} alt="" className="h-3.5 w-3.5" />
                          </button>
                        ) : (
                          row.challanNumber
                        )}
                      </td>
                      <td>{row.status}</td>

                      <td>
                        <div className="flex items-center justify-center gap-2">
                          <Link
                            to={`${viewDetailsBase}?trip_id=${encodeURIComponent(row.id)}${routeId ? `&route_id=${encodeURIComponent(routeId)}` : ""}${viewTripReturnQuery}`}
                          >
                            <img src={EyeIcon} className="h-7 min-w-[22px]" alt="View" />
                          </Link>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="flex gap-x-4 items-center mt-4">
          <select
            className="p-2 shadow-sm outline-none"
            value={itemsPerPage}
            onChange={(e) => {
              setItemsPerPage(Number(e.target.value));
              setCurrentPage(1);
            }}
            disabled={!routeId}
          >
            <option value={10}>10</option>
            <option value={20}>20</option>
          </select>
          <div className="text-sm text-gray-500 ml-auto">
            {routeId ? totalRecords : 0} records found
          </div>
          <div className="flex items-center gap-2 pr-8">
            <button
              type="button"
              onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
              disabled={currentPage === 1 || !routeId || tripsLoading}
              className={`!bg-primary text-white w-[35px] h-[35px] rounded-full disabled:opacity-50 inline-flex justify-center items-center ${
                currentPage === 1 ? "" : "cursor-pointer"
              }`}
            >
              <img src={LeftAngle} className="invert grayscale brightness-100" alt="" />
            </button>
            <PageNumberInput
              page={currentPage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
              className="h-[35px] w-12 rounded border border-gray-300 text-center text-sm font-UbuntuMedium shadow-sm outline-none disabled:cursor-not-allowed disabled:bg-gray-100 disabled:opacity-70"
            />
            <span className="text-sm text-gray-600">of {totalPages}</span>
            <button
              type="button"
              onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
              disabled={currentPage === totalPages || !routeId || tripsLoading}
              className={`!bg-primary text-white w-[35px] h-[35px] rounded-full disabled:opacity-50 inline-flex justify-center items-center ${
                currentPage === totalPages ? "" : "cursor-pointer"
              }`}
            >
              <img src={RightAngle} className="invert grayscale brightness-100" alt="" />
            </button>
          </div>
        </div>
      </div>

      {challanModalOpen
        ? createPortal(
            <div
              className="fixed inset-0 z-[10050] flex min-h-dvh w-full items-center justify-center overflow-y-auto bg-black/50 p-4"
              role="dialog"
              aria-modal="true"
              aria-labelledby="challan-modal-title"
              onClick={(e) => {
                if (e.target === e.currentTarget && !challanSaving) closeChallanModal();
              }}
            >
              <div
                className="w-full max-w-md shrink-0 rounded-lg bg-white p-6 shadow-lg"
                onClick={(e) => e.stopPropagation()}
              >
                <h2
                  id="challan-modal-title"
                  className="text-lg font-UbuntuMedium text-gray-900 mb-4"
                >
                  Update challan number
                </h2>
                <label
                  className="mb-1 block text-sm text-gray-600"
                  htmlFor="challan-input"
                >
                  Challan number
                </label>
                <input
                  id="challan-input"
                  type="text"
                  inputMode="text"
                  autoComplete="off"
                  className={`form-control ${challanError ? "border-danger" : ""}`}
                  value={challanInput}
                  maxLength={CHALLAN_MAX_LENGTH}
                  onChange={(e) => {
                    const raw = e.target.value;
                    const normalized = normalizeChallanInput(raw);
                    setChallanInput(normalized);
                    if (raw !== normalized) {
                      setChallanError("Only letters and numbers are allowed.");
                    } else if (normalized && !isValidChallanNumber(normalized)) {
                      setChallanError(
                        "Challan number must include both letters and numbers."
                      );
                    } else {
                      setChallanError("");
                    }
                  }}
                  placeholder="Enter Challan number"
                  pattern="[A-Za-z0-9]*"
                  title="Letters and numbers only; must include both letters and numbers"
                  disabled={challanSaving}
                  autoFocus
                />
                {challanError ? (
                  <p className="text-danger small mt-1 mb-4">{challanError}</p>
                ) : (
                  <div className="mb-4" />
                )}
                <div className="flex justify-end gap-2">
                  <button
                    type="button"
                    className="rounded-full border border-gray-300 px-5 py-2 text-sm"
                    onClick={closeChallanModal}
                    disabled={challanSaving}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="rounded-full !bg-primary px-5 py-2 text-sm text-white disabled:opacity-60"
                    onClick={submitChallanUpdate}
                    disabled={challanSaving || !canSubmitChallan}
                  >
                    {challanSaving ? "Saving…" : "Submit"}
                  </button>
                </div>
              </div>
            </div>,
            document.body
          )
        : null}

      <Toastify />
    </>
  );
};

export default ViewTrip;
